import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterConfigsDto,
  NavigationCustomizationItemDto,
} from '@asset-metrix/portal-gateway-client-js';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  fetchPageFilterData,
  PageFilterQueryKey,
} from '../../api/portalGateway/pagefilterData';
import { useStore } from '../../store';
import { useFilterSelectionsForQuery } from '../../store/hooks/useFilterSelections';
import { prependRoleId } from '../../utils/prependRoleId';

import { FiltersComponent } from './FiltersComponent';
import { validateFilters } from './validateFilters';

export type PageFilterProps = {
  activeDynamicNavigationItem?: NavigationCustomizationItemDto;
  className?: string;
  config?: FilterConfigsDto;
  embedded?: boolean;
  filterKey: string;
  pageId: string;
};

export const PageFilters = ({
  activeDynamicNavigationItem,
  className,
  config,
  embedded,
  filterKey,
  pageId,
}: PageFilterProps) => {
  const { t } = useTranslation();

  const [
    isFilterDataLoaded,
    isFilterDataInitialised,
    updateFilterData,
    resetFilterSelection,
    setPageFilterLoading,
  ] = useStore(
    useShallow((state) => [
      state.filterData[filterKey]?.meta.loaded ?? false,
      state.filterData[filterKey]?.meta.initialised ?? false,
      state.updateFilterData,
      state.resetFilterSelection,
      state.setPageFilterLoading,
    ])
  );

  const pageFilterSelections = useFilterSelectionsForQuery(filterKey);

  const { data, error, isFetching } = useQuery({
    queryKey: prependRoleId<PageFilterQueryKey>([
      'pageFilters',
      { pageId, filterKey },
      pageFilterSelections,
    ]),
    queryFn: fetchPageFilterData,
    enabled: config != null && !isFilterDataLoaded && isFilterDataInitialised,
  });

  useEffect(() => {
    setPageFilterLoading(isFetching || !isFilterDataLoaded);
    return () => setPageFilterLoading(false);
  }, [isFilterDataLoaded, isFetching, setPageFilterLoading]);

  useEffect(() => {
    if (data?.data != null && config != null) {
      const filters = validateFilters(
        config,
        data,
        activeDynamicNavigationItem
      );
      // set data for all filters after validation
      updateFilterData(filterKey, filters);
    }
  }, [
    activeDynamicNavigationItem,
    config,
    filterKey,
    data,
    updateFilterData,
    // Added as a dependency so that data is set on the store after a filter reset, no matter if it's changed or not
    isFilterDataLoaded,
  ]);

  if (config == null) {
    return undefined;
  }

  const visibleFilters = config.filters.filter((f) => !f.hidden);

  // all page filters are hidden (but selection should still apply!)
  if (visibleFilters.length === 0) {
    return undefined;
  }

  const resetFilterSelectionWrapper = (entityId: string) => {
    // omit hidden page filter keys from reset
    const hiddenPageFilterKeys = config.filters
      .filter((fil) => fil.hidden)
      .map((f) => f.key);
    return resetFilterSelection(entityId, undefined, hiddenPageFilterKeys);
  };

  return (
    <FiltersComponent
      className={className}
      config={{ ...config, filters: visibleFilters }}
      entityId={filterKey}
      error={error}
      isFetching={isFetching}
      resetFilterSelection={resetFilterSelectionWrapper}
      translate={t}
      embedded={embedded}
    />
  );
};
